import React, { createContext, useState, useContext, useEffect } from "react";
import { onSnapshot, doc } from "firebase/firestore";
import { auth, db } from "../firebase";

// Create the context with a default value that matches the shape of the context
const SubscriptionContext = createContext({
  subscription: null,
  loading: true,
  setSubscription: () => {},
});

// Custom hook to use the subscription context
export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscriptionContext must be used within a SubscriptionProvider");
  }
  return context;
};

// Provider component
export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // El valor que retorna el callback de onAuthStateChanged no es un cleanup
    // de React (Firebase lo ignora), así que el unsubscribe del snapshot
    // anterior hay que guardarlo aparte y cancelarlo a mano en cada cambio de
    // auth (y al desmontar) — si no, el listener de users/{uid} sigue activo
    // después de cerrar sesión y tira permission-denied contra las reglas.
    let unsubscribeSnapshot = null;

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }

      if (!user) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      unsubscribeSnapshot = onSnapshot(doc(db, "users", user.uid), (doc) => {
        if (doc.exists()) {
          setSubscription(doc.data().subscription || null);
        } else {
          setSubscription(null);
        }
        setLoading(false);
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  // ✅ Derivado directamente de subscription, siempre sincronizado.
  // El plan Free (ver subscription.js) dura 1 mes desde que se reclama;
  // Firestore nunca marca el vencimiento por sí solo, así que se detecta
  // comparando su endDate contra la fecha actual.
  const isFreePlanExpired =
    subscription?.planName === "Free" &&
    !!subscription?.endDate &&
    new Date(subscription.endDate) < new Date();

  // Un plan pago cancelado sigue vigente hasta el final del período que ya se
  // pagó (paidUntil, lo guarda cancelCompanySubscription). Mismo criterio que
  // activePlan en functions/publicationLimits.js.
  const isPaidPeriodLeft =
    subscription?.status === "CANCELLED" &&
    subscription?.planName !== "Free" &&
    !!subscription?.paidUntil &&
    new Date(subscription.paidUntil) > new Date();

  const isSubscriptionActive =
    (subscription?.status === "ACTIVE" && !isFreePlanExpired) || isPaidPeriodLeft;

  const value = {
    subscription,
    loading,
    setSubscription,
    isSubscriptionActive,
    isFreePlanExpired,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};